import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  X,
  LogOut,
  User,
  Phone,
  Calendar,
  AlertCircle,
} from 'lucide-react';
import './VisitorsPage.css';

const VisitorsPage = () => {
  // State management
  const [visitors, setVisitors] = useState([]);
  const [stats, setStats] = useState({ totalToday: 0, currentlyIn: 0, checkedOut: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [purposeFilter, setPurposeFilter] = useState('all');
  const [checkoutId, setCheckoutId] = useState(null);
  const [checkoutNotes, setCheckoutNotes] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    visitorName: '',
    phone: '',
    email: '',
    purpose: 'meeting',
    whomToMeet: '',
    department: '',
    idType: 'passport',
    idNumber: '',
    vehicleNumber: '',
  });

  const purposeColors = {
    meeting: '#3b82f6',
    delivery: '#f472b6',
    interview: '#10b981',
    official: '#b8860b',
    other: '#6b7280',
  };

  const purposeBgColors = {
    meeting: '#dbeafe',
    delivery: '#fbcfe8',
    interview: '#d1fae5',
    official: '#ede9fe',
    other: '#f3f4f6',
  };

  // Fetch visitors and stats
  useEffect(() => {
    fetchVisitors();
    fetchStats();
  }, []);

  const fetchVisitors = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/visitors');
      const data = response.data.items || response.data;
      setVisitors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching visitors:', err);
      setVisitors([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/visitors/stats');
      setStats(response.data || { totalToday: 0, currentlyIn: 0, checkedOut: 0 });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleAddVisitor = () => {
    setEditingId(null);
    setFormData({
      visitorName: '',
      phone: '',
      email: '',
      purpose: 'meeting',
      whomToMeet: '',
      department: '',
      idType: 'passport',
      idNumber: '',
      vehicleNumber: '',
    });
    setShowModal(true);
  };

  const handleEditVisitor = (visitor) => {
    setEditingId(visitor.id);
    setFormData({
      visitorName: visitor.visitorName || '',
      phone: visitor.phone || '',
      email: visitor.email || '',
      purpose: visitor.purpose || 'meeting',
      whomToMeet: visitor.whomToMeet || '',
      department: visitor.department || '',
      idType: visitor.idType || 'passport',
      idNumber: visitor.idNumber || '',
      vehicleNumber: visitor.vehicleNumber || '',
    });
    setShowModal(true);
  };

  const handleDeleteVisitor = async (visitorId) => {
    try {
      await api.delete(`/visitors/${visitorId}`);
      setVisitors(visitors.filter((v) => v.id !== visitorId));
      setDeleteConfirm(null);
      fetchStats();
    } catch (err) {
      console.error('Error deleting visitor:', err);
      setError('Failed to delete visitor');
    }
  };

  const handleCheckout = async () => {
    if (!checkoutId) return;
    try {
      await api.put(`/visitors/${checkoutId}/checkout`, { notes: checkoutNotes });
      setCheckoutId(null);
      setCheckoutNotes('');
      fetchVisitors();
      fetchStats();
    } catch (err) {
      console.error('Error checking out visitor:', err);
      setError('Failed to checkout visitor');
    }
  };

  const handleSaveVisitor = async (e) => {
    e.preventDefault();

    try {
      const payload = { ...formData };

      if (editingId) {
        await api.put(`/visitors/${editingId}`, payload);
      } else {
        await api.post('/visitors', payload);
      }

      setShowModal(false);
      fetchVisitors();
      fetchStats();
    } catch (err) {
      console.error('Error saving visitor:', err);
      setError('Failed to save visitor');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredVisitors = visitors.filter((visitor) => {
    const statusMatch = statusFilter === 'all' || visitor.status === statusFilter;
    const purposeMatch = purposeFilter === 'all' || visitor.purpose === purposeFilter;
    return statusMatch && purposeMatch;
  });

  const StatCard = ({ icon: Icon, label, value, bgColor }) => (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      }}
    >
      <div
        style={{
          backgroundColor: bgColor,
          width: '48px',
          height: '48px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={24} style={{ color: '#ffffff' }} />
      </div>
      <div>
        <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>
          {label}
        </p>
        <p style={{ margin: '4px 0 0 0', fontSize: '24px', fontWeight: '700', color: '#111827' }}>
          {value}
        </p>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#111827' }}>
            Visitor Management
          </h1>
          <p style={{ margin: 0, color: '#6b7280' }}>Track and manage school visitors</p>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <StatCard icon={Users} label="Total Today" value={stats.totalToday} bgColor="#3b82f6" />
          <StatCard icon={User} label="Currently In" value={stats.currentlyIn} bgColor="#10b981" />
          <StatCard icon={LogOut} label="Checked Out" value={stats.checkedOut} bgColor="#6b7280" />
        </div>

        {/* Filter Bar */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="all">All Status</option>
            <option value="checked-in">Checked In</option>
            <option value="checked-out">Checked Out</option>
          </select>

          <select
            value={purposeFilter}
            onChange={(e) => setPurposeFilter(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="all">All Purposes</option>
            <option value="meeting">Meeting</option>
            <option value="delivery">Delivery</option>
            <option value="interview">Interview</option>
            <option value="official">Official</option>
            <option value="other">Other</option>
          </select>

          <button
            onClick={handleAddVisitor}
            style={{
              padding: '10px 16px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginLeft: 'auto',
            }}
          >
            <Plus size={18} />
            Register Visitor
          </button>
        </div>

        {/* Visitors Table */}
        <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              Loading visitors...
            </div>
          ) : filteredVisitors.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
              <Users size={48} style={{ margin: '0 auto 16px', color: '#d1d5db' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#9ca3af' }}>No visitors found</h3>
              <p style={{ margin: 0, fontSize: '14px' }}>
                {statusFilter !== 'all' || purposeFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Register your first visitor to get started'}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                }}
              >
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Visitor Name
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Phone
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Purpose
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Whom to Meet
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Check-in Time
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Status
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVisitors.map((visitor) => {
                    const purposeColor = purposeColors[visitor.purpose] || purposeColors.other;
                    const purposeBgColor = purposeBgColors[visitor.purpose] || purposeBgColors.other;
                    const statusColor = visitor.status === 'checked-in' ? '#10b981' : '#6b7280';
                    const statusBgColor = visitor.status === 'checked-in' ? '#d1fae5' : '#f3f4f6';

                    return (
                      <tr key={visitor.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 16px', color: '#111827', fontWeight: '500' }}>
                          {visitor.visitorName}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Phone size={14} />
                            {visitor.phone}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span
                            style={{
                              backgroundColor: purposeBgColor,
                              color: purposeColor,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              textTransform: 'capitalize',
                              fontWeight: '500',
                            }}
                          >
                            {visitor.purpose}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                          {visitor.whomToMeet}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Calendar size={14} />
                            {visitor.checkInTime ? formatTime(visitor.checkInTime) : '-'}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span
                            style={{
                              backgroundColor: statusBgColor,
                              color: statusColor,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              textTransform: 'capitalize',
                              fontWeight: '500',
                            }}
                          >
                            {visitor.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {visitor.status === 'checked-in' && (
                              <button
                                onClick={() => setCheckoutId(visitor.id)}
                                style={{
                                  padding: '6px 10px',
                                  backgroundColor: '#10b981',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                <LogOut size={14} />
                                Checkout
                              </button>
                            )}
                            <button
                              onClick={() => handleEditVisitor(visitor)}
                              style={{
                                padding: '6px 10px',
                                backgroundColor: '#f3f4f6',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(visitor.id)}
                              style={{
                                padding: '6px 10px',
                                backgroundColor: '#fee2e2',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Trash2 size={14} style={{ color: '#dc2626' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '400px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Delete Visitor Record?
              </h2>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280' }}>
                This action cannot be undone. Are you sure you want to delete this visitor record?
              </p>
            </div>
            <div
              style={{
                padding: '16px 24px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteVisitor(deleteConfirm)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {checkoutId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setCheckoutId(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '500px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Checkout Visitor
              </h2>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  Checkout Notes (Optional)
                </label>
                <textarea
                  value={checkoutNotes}
                  onChange={(e) => setCheckoutNotes(e.target.value)}
                  placeholder="Add any notes about the visit"
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                  }}
                />
              </div>
            </div>
            <div
              style={{
                padding: '16px 24px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={() => setCheckoutId(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCheckout}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#10b981',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Confirm Checkout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Visitor Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                {editingId ? 'Edit Visitor' : 'Register Visitor'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveVisitor}>
              <div style={{ padding: '24px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Visitor Name *
                  </label>
                  <input
                    type="text"
                    name="visitorName"
                    value={formData.visitorName}
                    onChange={handleFormChange}
                    placeholder="Enter full name"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      placeholder="Enter phone number"
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="Enter email"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Purpose *
                    </label>
                    <select
                      name="purpose"
                      value={formData.purpose}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="meeting">Meeting</option>
                      <option value="delivery">Delivery</option>
                      <option value="interview">Interview</option>
                      <option value="official">Official</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Whom to Meet *
                    </label>
                    <input
                      type="text"
                      name="whomToMeet"
                      value={formData.whomToMeet}
                      onChange={handleFormChange}
                      placeholder="Enter person name"
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Department
                  </label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleFormChange}
                    placeholder="Enter department"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      ID Type
                    </label>
                    <select
                      name="idType"
                      value={formData.idType}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="passport">Passport</option>
                      <option value="license">License</option>
                      <option value="aadhar">Aadhar</option>
                      <option value="pan">PAN</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      ID Number
                    </label>
                    <input
                      type="text"
                      name="idNumber"
                      value={formData.idNumber}
                      onChange={handleFormChange}
                      placeholder="Enter ID number"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Vehicle Number
                  </label>
                  <input
                    type="text"
                    name="vehicleNumber"
                    value={formData.vehicleNumber}
                    onChange={handleFormChange}
                    placeholder="Enter vehicle number if applicable"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: '16px 24px',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {editingId ? 'Update' : 'Register'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorsPage;
